import { useQueries } from 'react-query';
import { useModal } from '@ebay/nice-modal-react';
import { useRouteMatch, useHistory } from 'react-router-dom';

// Import hooks
import { useAppSelector } from 'store/hooks';

// Import helpers
import { fetchLastPaymentRedirectUrls } from 'helpers/api/payments';
import { fetchCurrentSubscription, fetchCalculatePrice } from 'helpers/configurator';
import { fetchDocumentByAlias } from 'helpers/api/documents';
import { transformSubscription, transformLastPayments } from 'components/views/offer_configurator/helpers/packets';

// Import components
import { AntNiceModal } from 'components/views/offer_configurator/components/common/modals';

// Import routes
import { MAIN_PATHS } from 'components/routes/app_routes/paths';
import { USER_PATHS } from 'components/routes/user_routes/paths';

// Import types
import type { Document } from 'types/api/documents';
import type { LastPaymentsRedirectUrl } from 'types/api/payments';
import { CalculatePriceAction } from 'types/api/packetsSubscription/calculatePrice';

export const useResources = (isConfigurator = true) => {
	const token = useAppSelector(({ subscriber }) => subscriber.token);
	const packets = useAppSelector(({ configurator }) => configurator.packets);
	const history = useHistory();
	const infoModal = useModal(AntNiceModal);
	const match = useRouteMatch([MAIN_PATHS.OFFER_CONFIGURATOR, USER_PATHS.PACKETS]);

	const isAuth = Boolean(token);

	const resources = useQueries([
		{
			queryKey: ['last_payments', token],
			queryFn: fetchLastPaymentRedirectUrls,
			enabled: isAuth,
			cacheTime: 0,
			select: transformLastPayments,
			onSuccess: (data: LastPaymentsRedirectUrl | null) => {
				if (data) {
					if (!match?.isExact) {
						infoModal.show({
							namespace: 'transaction',
							title: 'modal_last_payment_title',
							description: { key: 'modal_last_payment_description', values: data },
							confirmText: 'button_close'
						});

						history.replace(MAIN_PATHS.OFFER_CONFIGURATOR);
					}
				}
			}
		},
		{
			queryKey: ['current_subscription', token],
			queryFn: fetchCurrentSubscription,
			enabled: isAuth,
			cacheTime: 0,
			select: transformSubscription
		},
		{
			queryKey: ['main_packets_description'],
			queryFn: () => fetchDocumentByAlias('packets_main_description'),
			select: (data: Document | null): string | null => {
				if (!data) return null;

				return data.content;
			},
			enabled: isConfigurator
		},
		{
			enabled: isConfigurator && !packets.isLoading,
			queryKey: ['configurator_packets_price', token],
			queryFn: () =>
				fetchCalculatePrice(packets.main.concat(packets.premium), CalculatePriceAction.New, Boolean(token)).then((data) => data.products),
			initialData: []
		}
	]);

	const isResourcesLoading = resources.some(({ status }) => status === 'loading');

	return { resources, isResourcesLoading };
};
